import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button, InlineAlert, SearchableSelect, TextArea } from '../../components/BaseComponents'
import CanonicalModal from '../../components/BaseComponents/CanonicalModal/CanonicalModal'
import Label from '../../components/BaseComponents/Label'
import AdminCreateCompany from '../../components/Admin/CreateCompany/CreateCompany'
import useApi from '../../hooks/useApi'
import useAddNotification from '../../hooks/useAddNotification'
import useCleanup from '../../hooks/useCleanup'
import { SelectDropdown } from '../../types/SelectDropdown'
import { getApiErrorMessage, getCSRFToken, hasApiErrorCode } from '../../lib/utils'

export type ReviewableUser = {
	id: string
	fullName: string
	email: string
}

type ReviewUpgradeRequestModalProps = {
	user: ReviewableUser | null
	onClose: () => void
	onReviewed: () => void
	onUnconfirmed?: () => void
}

function ReviewUpgradeRequestModal({ user, onClose, onReviewed, onUnconfirmed }: ReviewUpgradeRequestModalProps) {
	const api = useApi()
	const notify = useAddNotification()
	const [companyOptions, setCompanyOptions] = useState<Array<SelectDropdown>>([])
	const [createdOptions, setCreatedOptions] = useState<Array<SelectDropdown>>([])
	const [companyId, setCompanyId] = useState('')
	const [reason, setReason] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [uncertain, setUncertain] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [createOpen, setCreateOpen] = useState(false)
	const [loadingOptions, setLoadingOptions] = useState(false)
	const [lookupError, setLookupError] = useState('')
	const [loadedUserId, setLoadedUserId] = useState<string>()
	const [loadedApi, setLoadedApi] = useState<ReturnType<typeof useApi>>()
	const lookupController = useRef<AbortController>()
	const reviewRequest = useRef<{ controller: AbortController; submitted: boolean }>()
	const uncertainRef = useRef(false)
	const notifyRef = useRef(notify)
	notifyRef.current = notify
	const context = useRef({ userId: user?.id, api, generation: 0 })
	if (context.current.userId !== user?.id || context.current.api !== api) {
		context.current = { userId: user?.id, api, generation: context.current.generation + 1 }
	}
	const abortReview = useCallback(() => {
		const request = reviewRequest.current
		if (!request) return false
		reviewRequest.current = undefined
		request.controller.abort()
		if (request.submitted) {
			notifyRef.current({ body: 'The review was interrupted and may already have been processed. Refresh the user list and check the request before trying again.' })
			return true
		}
		return false
	}, [])

	useEffect(() => () => {
		context.current.generation += 1
		lookupController.current?.abort()
		abortReview()
	}, [abortReview])

	const loadOptions = useCleanup(() => {
		const generation = context.current.generation
		setLoadedUserId(user?.id)
		setLoadedApi(api)
		setCompanyOptions([])
		setCompanyId((selected) => createdOptions.some((option) => option.value === selected) ? selected : '')
		setLookupError('')
		if (!user) {
			setLoadingOptions(false)
			return
		}
		const controller = new AbortController()
		lookupController.current = controller
		setLoadingOptions(true)
		const run = async () => {
			try {
				const body = await api.get(
					`company?${new URLSearchParams({ page: '1', limit: '1000', orderBy: 'name', direction: 'asc' })}`,
					{ signal: controller.signal }
				)
				if (controller.signal.aborted || generation !== context.current.generation) return
				const companies: unknown = body.companies?.data
				if (body.success === false || !Array.isArray(companies) || companies.some((company) =>
					typeof company?.id !== 'string' || typeof company?.name !== 'string'
				)) {
					throw new Error('Invalid organisation list')
				}
				setCompanyOptions(companies.map((company) => ({ label: company.name, value: company.id })))
			} catch (err) {
				if (!controller.signal.aborted && generation === context.current.generation) {
					setLookupError(getApiErrorMessage(err, 'Please try again.'))
				}
			} finally {
				if (!controller.signal.aborted && generation === context.current.generation) setLoadingOptions(false)
			}
		}
		run()
		return () => controller.abort()
	})

	// Clear the previous user's review fields before the new context is painted.
	useLayoutEffect(() => {
		setCompanyId('')
		setCreatedOptions([])
		setReason('')
		setError(null)
		setSubmitting(false)
		setUncertain(false)
		uncertainRef.current = false
		setCreateOpen(false)
		loadOptions()
		return () => { abortReview() }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id, api, abortReview])

	const optionsReady = !!user && loadedUserId === user.id && loadedApi === api && !loadingOptions && !lookupError
	const options = [
		...companyOptions,
		...createdOptions.filter((option) => !companyOptions.some((item) => item.value === option.value)),
	]

	const close = () => {
		context.current.generation += 1
		lookupController.current?.abort()
		const unconfirmed = abortReview() || uncertainRef.current
		uncertainRef.current = false
		setCompanyId('')
		setReason('')
		setError(null)
		setSubmitting(false)
		setUncertain(false)
		onClose()
		if (unconfirmed) onUnconfirmed?.()
	}

	const submit = async (action: 'approve' | 'deny') => {
		if (!user || uncertainRef.current || reviewRequest.current ||
			(action === 'approve' && (!optionsReady || !companyId))) {
			return
		}
		const generation = context.current.generation
		const controller = new AbortController()
		const request = { controller, submitted: false }
		reviewRequest.current = request
		setSubmitting(true)
		setError(null)
		const markUncertain = (detail: string) => {
			const message = `${detail} The review may already have been processed. Refresh the user list and check the request before trying again.`
			uncertainRef.current = true
			setUncertain(true)
			setError(message)
			notifyRef.current({ body: message })
		}
		try {
			const csrf = await getCSRFToken(controller.signal)
			if (controller.signal.aborted || generation !== context.current.generation) return
			request.submitted = true
			const body = await api.post(`admin/training-users/${user.id}/approve-upgrade`, {
				_csrf: csrf,
				action,
				companyId: action === 'approve' ? companyId : undefined,
				reason: reason.trim() || undefined,
			}, { signal: controller.signal })
			if (controller.signal.aborted || generation !== context.current.generation) return
			if (body.success !== true) {
				markUncertain(getApiErrorMessage({ body }, 'The review could not be confirmed.'))
				return
			}
			reviewRequest.current = undefined
			close()
			onReviewed()
		} catch (err) {
			if (!controller.signal.aborted && generation === context.current.generation) {
				const denied = hasApiErrorCode(err, 'READ_ONLY_ACCESS') || hasApiErrorCode(err, 'ACCESS_AUDIT_UNAVAILABLE')
				if (request.submitted && !denied) {
					markUncertain(getApiErrorMessage(err, 'The review could not be confirmed.'))
				} else {
					setError(getApiErrorMessage(err, 'Failed to submit the review. Please try again.'))
				}
			}
		} finally {
			if (reviewRequest.current === request) reviewRequest.current = undefined
			if (!controller.signal.aborted && generation === context.current.generation) setSubmitting(false)
		}
	}

	return (
		<>
			{/* Hidden (not stacked) while the create modal is open, so only one dialog is ever visible; form state is kept. */}
			<CanonicalModal
				open={user !== null && !createOpen}
				onClose={() => {
					// Hiding for the create modal also fires the after-close callback; that must not discard the review.
					if (!createOpen) close()
				}}
				size='small'
				title='Review upgrade request'
				subtitle={user ? `${user.fullName} (${user.email})` : undefined}
				actions={
					<div className='flex space-x-3'>
						<Button
							type='Primary'
							text='Approve'
							disabled={submitting || uncertain || !optionsReady || !companyId}
							onClick={() => submit('approve')}
						/>
						<Button
							type='Outlined'
							text='Deny'
							disabled={submitting || uncertain}
							onClick={() => submit('deny')}
						/>
					</div>
				}
			>
				<div className='mt-6'>
					<Label className='block'>Assign to an existing organisation</Label>
					<div className='flex items-start space-x-3'>
						<div className='grow'>
							<SearchableSelect
								options={optionsReady ? options : []}
								isDisabled={!optionsReady || submitting || uncertain}
								value={optionsReady ? options.find((option) => option.value === companyId) : null}
								onChange={(option) => setCompanyId(option?.value ?? '')}
							/>
						</div>
						<Button
							type='Outlined'
							text='Create new'
							disabled={submitting || uncertain}
							context='form-field'
							icon='fa-solid fa-plus'
							hasTrailingIcon
							onClick={() => setCreateOpen(true)}
						/>
					</div>
					<div className='text-charcoal-100 text-sm mt-1'>
						The new organisation is selected automatically once it has been created.
					</div>
					{lookupError && (
						<InlineAlert type='error' className='mt-4'>
							<p>Could not load organisations.</p>
							<p>{lookupError}</p>
							<Button type='Primary' text='Retry loading organisations' onClick={loadOptions} />
						</InlineAlert>
					)}
				</div>

				<div className='mt-6'>
					<Label className='block'>Reason (optional)</Label>
					<TextArea
						rows={2}
						value={reason}
						onChange={(e) => setReason(e.target.value)}
					/>
					<div className='text-charcoal-100 text-sm mt-1'>
						If you deny this request, the reason will be emailed to the user.
					</div>
				</div>

				{error && <InlineAlert type='error' className='mt-2'>{error}</InlineAlert>}
			</CanonicalModal>
			{createOpen && (
				<AdminCreateCompany
					open
					onClose={() => setCreateOpen(false)}
					onCreated={(company) => {
						setCreatedOptions((options) =>
							options.some((option) => option.value === company.id)
								? options
								: [...options, { label: company.name, value: company.id }]
						)
						setCompanyId(company.id)
					}}
				/>
			)}
		</>
	)
}

export default ReviewUpgradeRequestModal
